/**
 * Logo Upload Component
 * Handles logo upload with preview and validation
 */

import React, { useCallback, useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Upload, X, Image, AlertCircle } from 'lucide-react';
import { validateLogoFile } from '../../utils/validation';
import { useDeleteLogo } from '../../hooks/business/useWorkplaceDetails';

interface LogoUploadComponentProps {
  currentLogoUrl?: string | null;
  previewUrl?: string | null;
  onLogoSelect: (file: File) => void;
  onLogoRemove: () => void;
  disabled?: boolean;
  error?: string;
}

export const LogoUploadComponent: React.FC<LogoUploadComponentProps> = ({
  currentLogoUrl,
  previewUrl,
  onLogoSelect,
  onLogoRemove,
  disabled = false,
  error,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deleteMutation = useDeleteLogo();

  const handleFileSelect = useCallback(
    (file: File) => {
      // Validate the file
      const validation = validateLogoFile(file);
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid file');
        return;
      }

      setValidationError(null);
      onLogoSelect(file);
    },
    [onLogoSelect]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      
      if (disabled) return;

      const files = Array.from(event.dataTransfer.files);
      const file = files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [disabled, handleFileSelect]
  );

  const handleRemoveCurrentLogo = async () => {
    try {
      await deleteMutation.mutateAsync();
    } catch (error) {
      console.error('Error deleting logo:', error);
    }
  };

  const handleRemovePreview = () => {
    setValidationError(null);
    onLogoRemove();
  };

  const displayError = error || validationError;
  const hasPreview = previewUrl || currentLogoUrl;
  const showUploadArea = !hasPreview || previewUrl;

  return (
    <div className="space-y-4">
      <Label>Business Logo</Label>
      <p className="text-sm text-muted-foreground">
        Upload your business logo (PNG, JPG, or GIF, max 2MB)
      </p>

      {/* Current Logo Display */}
      {currentLogoUrl && !previewUrl && (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img
                src={currentLogoUrl}
                alt="Current business logo"
                className="h-16 w-16 object-contain rounded border"
              />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Current Logo</p>
              <p className="text-xs text-muted-foreground">
                Click "Remove" to delete or drag a new file to replace
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveCurrentLogo}
              disabled={disabled || deleteMutation.isPending}
            >
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* Preview Display */}
      {previewUrl && (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img
                src={previewUrl}
                alt="Logo preview"
                className="h-16 w-16 object-contain rounded border"
              />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">New Logo Preview</p>
              <p className="text-xs text-muted-foreground">
                This will replace your current logo when you save
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemovePreview}
              disabled={disabled}
            >
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {showUploadArea && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary hover:bg-primary/5'}
            ${displayError ? 'border-red-300 bg-red-50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif"
            onChange={handleFileChange}
            disabled={disabled}
            className="hidden"
          />
          
          <div className="space-y-2">
            {isDragOver ? (
              <>
                <Upload className="h-8 w-8 mx-auto text-primary" />
                <p className="text-sm font-medium">Drop your logo here</p>
              </>
            ) : (
              <>
                <Image className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm font-medium">
                  Drag and drop your logo here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, or GIF up to 2MB
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {displayError && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{displayError}</span>
        </div>
      )}

      {/* Upload Button (Alternative to drag-and-drop) */}
      {showUploadArea && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="w-full sm:w-auto"
          >
            <Upload className="h-4 w-4 mr-2" />
            Choose File
          </Button>
        </div>
      )}
    </div>
  );
};

export default LogoUploadComponent;
